import { SanityImageSource } from '@sanity/image-url/lib/types/types'
import { urlFor } from './sanity'
import { SanityImage } from '@/types'

/**
 * Default image sizes for responsive images
 */
export const DEFAULT_IMAGE_SIZES = {
    small: 400,
    medium: 800,
    large: 1200,
    thumbnail: 200,
}

/**
 * Default image quality
 */
export const DEFAULT_IMAGE_QUALITY = 80

/**
 * Default blur-up placeholder size
 */
export const PLACEHOLDER_SIZE = 20

/**
 * Generate optimized image URL with specified parameters
 */
export function getOptimizedImageUrl(
    image: SanityImageSource,
    {
        width = DEFAULT_IMAGE_SIZES.medium,
        height,
        quality = DEFAULT_IMAGE_QUALITY,
        format = 'webp',
    }: {
        width?: number
        height?: number
        quality?: number
        format?: 'webp' | 'jpg' | 'png' | 'auto'
    } = {}
) {
    let imageUrlBuilder = urlFor(image).auto('format').quality(quality)

    if (format && format !== 'auto') {
        imageUrlBuilder = imageUrlBuilder.format(format as any)
    }

    if (width) {
        imageUrlBuilder = imageUrlBuilder.width(width)
    }

    if (height) {
        imageUrlBuilder = imageUrlBuilder.height(height)
    }

    return imageUrlBuilder.url()
}

/**
 * Generate a low-resolution placeholder image URL
 */
export function getPlaceholderImageUrl(image: SanityImageSource) {
    return urlFor(image)
        .width(PLACEHOLDER_SIZE)
        .height(PLACEHOLDER_SIZE)
        .quality(20)
        .blur(10)
        .format('webp')
        .url()
}

/**
 * Generate responsive image URLs for srcSet
 */
export function getResponsiveImageSrcSet(
    image: SanityImageSource,
    widths: number[] = [400, 800, 1200, 1600],
    options: { quality?: number; format?: 'webp' | 'jpg' | 'png' | 'auto' } = {}
) {
    return widths
        .map(
            (width) =>
                `${getOptimizedImageUrl(image, { width, ...options })} ${width}w`
        )
        .join(', ')
}

/**
 * Get image alt text with fallback
 */
export function getImageAlt(image: SanityImage, fallback: string = 'Image') {
    return image?.alt || fallback
}

/**
 * Check if an image exists
 */
export function hasValidImage(image?: SanityImage | null): boolean {
    return Boolean(image && image.asset && image.asset._ref)
}